import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { csspSearchCache } from "@/features/cssp/csspSearchCache";
import { createCsspSearchCacheContext } from "@/features/cssp/csspSearchContext";
import appStore from "@/stores/appStore";
import { getCsspCity, resolveCsspCity } from "@/features/cssp/csspCity";
import { useCsspSuppliersStore } from "@/features/cssp/CsspSuppliersContext";

/** Keeps the current-tab CSSP cache honest while the CSSP route is unmounted. */
export const CsspSearchCacheLifecycle = observer(() => {
  const selectedCity = appStore.selectedCity;
  const isLoggedIn = appStore.isLoginIn;
  const authSessionGeneration = appStore.authSessionGeneration;
  const user = appStore.user ?? appStore.userInfo;
  const suppliersStore = useCsspSuppliersStore();
  const citiesSignature = appStore.cities.map((city) => `${city.name}:${city.id}`).join("|");
  const cities = appStore.cities;
  const context = useMemo(
    () => createCsspSearchCacheContext(
      selectedCity,
      isLoggedIn,
      user,
      cities,
      authSessionGeneration
    ),
    [authSessionGeneration, cities, isLoggedIn, selectedCity, user]
  );
  // This same key is passed to the route flow, so a prefetch and a route
  // search can join the store's one in-flight request.
  const sessionKey = context.sessionKey ?? context.userFingerprint;

  useEffect(() => {
    csspSearchCache.clearIfContextChanged(context);
  }, [context]);

  useEffect(() => {
    let cancelled = false;
    const supportedCity = getCsspCity(selectedCity);
    if (!supportedCity) {
      suppliersStore.setContext(null, sessionKey);
      return;
    }

    const prepare = async () => {
      if (!appStore.cities.length) {
        try {
          await appStore.getCities();
        } catch {
          return;
        }
      }
      if (cancelled) return;
      const resolved = resolveCsspCity(selectedCity, appStore.cities);
      suppliersStore.setContext(resolved?.id ?? null, sessionKey);
      if (isLoggedIn && resolved) {
        void suppliersStore.ensureSuppliers(resolved.id).catch(() => undefined);
      }
    };
    void prepare();
    return () => { cancelled = true; };
  }, [citiesSignature, isLoggedIn, selectedCity, sessionKey, suppliersStore]);

  useEffect(() => {
    const clearExpired = () => csspSearchCache.clearExpired();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") clearExpired();
    };

    window.addEventListener("pageshow", clearExpired);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", clearExpired);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return null;
});
